import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { getProductPrice, getProductSpecialPrices } from '../../ProductActions';
import { getUserGroup, getUserGroupOption } from '../../../User/UserGroupActions';
import { getCartItemByProduct, getCartItemFromProduct, setCartItem } from '../../../Cart/CartActions';
import { getReferral } from '../../../App/AppActions';

import Price from '../../../Currency/components/view/Price';
import Icon from '../../../../components/Content/Icon';
import { getLoggedUser, getUserPreference } from '../../../User/UserActions';
import { formatPrice } from '../../../../util/price';

function ProductSpecialPriceQuantityInfo({ dispatch, product, item, user = null, userGroup = null, referral = null }) {
	const displayPriceHT = getUserGroupOption(userGroup, 'displayPriceHT');
	const productNormalPrice = getProductPrice(product, 1, userGroup, !displayPriceHT, referral);
	const currentQuantity = item?.quantity || 0;

	// Each quantity special-price becomes an "unlockable" tier card. Clicking a card bumps the
	// cart up to that quantity (only ever upward, like the original). The best tier is highlighted.
	const tiers = getProductSpecialPrices(product, displayPriceHT ? 'priceHT' : 'priceTTC', null, userGroup, referral)
		.filter(specialPrice => specialPrice.quantity)
		.reverse()
		.filter(specialPrice => specialPrice.quantity > Math.max(currentQuantity, 1))
		.map(specialPrice => {
			const total = getProductPrice(product, specialPrice.quantity, userGroup, !displayPriceHT, referral);
			const unit = total / specialPrice.quantity;
			const normalTotal = productNormalPrice * specialPrice.quantity;
			return {
				quantity: specialPrice.quantity,
				value: specialPrice.value,
				total,
				unit,
				normalTotal,
				savings: normalTotal - total,
				percent: Math.ceil(formatPrice(100 - ((unit / productNormalPrice) * 100))),
			};
		})
		.filter(tier => tier.value < productNormalPrice);

	const handleApplySpecialPriceQuantity = quantity => {
		if(currentQuantity < quantity) {
			dispatch(setCartItem(getCartItemFromProduct(product, quantity, getUserPreference(user), userGroup, { referral })));
		}
	};

	if(!tiers.length) {
		return null;
	}

	// The deepest discount (last tier) is the "best value" hero card.
	const bestPercent = Math.max(...tiers.map(tier => tier.percent));

	return (
		<div className="d-flex flex-column gap-2 mt-2">
			{tiers.map(tier => {
				const featured = tier.percent === bestPercent;
				return (
					<OverlayTrigger
						key={tier.quantity}
						placement="top"
						overlay={
							<Tooltip id={`${product.slug}-specialprice-${tier.quantity}`}>
								<FormattedMessage id="productUnitPriceInclTax" defaultMessage="Unit price incl.tax" />: <Price amount={tier.unit} />
							</Tooltip>
						}
					>
						<div
							role="button"
							tabIndex={0}
							onClick={() => handleApplySpecialPriceQuantity(tier.quantity)}
							onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && handleApplySpecialPriceQuantity(tier.quantity)}
							className={`position-relative d-flex align-items-center gap-2 gap-md-3 p-2 px-2 px-md-3 rounded-3 border ${featured ? 'border-2 border-success bg-white shadow-sm' : 'bg-light'}`}
						>
							<span className="d-none d-sm-inline-flex align-items-center justify-content-center rounded-circle bg-success text-white flex-shrink-0" style={{ width: '32px', height: '32px' }}>
								<Icon icon={featured ? 'gift' : 'tag'} />
							</span>
							<div className="flex-grow-1 lh-sm text-truncate" style={{ minWidth: 0 }}>
								<strong className="d-block text-dark text-truncate">
									<FormattedMessage id="productSpecialPriceQuantityFrom" defaultMessage="From {count} pieces" values={{ count: tier.quantity }} />
									{/* {featured && (
										<span className="badge ms-1 ms-md-2 rounded-pill bg-success text-uppercase align-middle" style={{ fontSize: '0.6rem', letterSpacing: '0.04em' }}>
											⭐<span className="d-none d-md-inline"> <FormattedMessage id="productOfferBestValue" defaultMessage="Best value" /></span>
										</span>
									)} */}
								</strong>
								<span className="small fw-bold text-success text-truncate">
									<FormattedMessage id="productOfferSave" defaultMessage="Save {amount}" values={{ amount: <Price amount={tier.savings} formatPrice /> }} />
								</span>
							</div>
							<span className="position-absolute top-0 end-0 badge rounded-pill bg-danger" style={{ transform: 'translate(1.5rem, -0.5rem)' }}>-{tier.percent}%</span>
						</div>
					</OverlayTrigger>
				);
			})}
		</div>
	);
}

ProductSpecialPriceQuantityInfo.propTypes = {
	dispatch: PropTypes.func.isRequired,
	product: PropTypes.object.isRequired,
	item: PropTypes.object,
	user: PropTypes.object,
	userGroup: PropTypes.object,
	referral: PropTypes.string,
};

const mapStateToProps = (store, props) => {
	return {
		item: getCartItemByProduct(store, props.product),
		user: getLoggedUser(store),
		userGroup: getUserGroup(store),
		referral: getReferral(store),
	};
};

export default connect(mapStateToProps)(ProductSpecialPriceQuantityInfo);
